import { MatSnackBar } from '@angular/material/snack-bar';
import { NavigationService } from 'src/app/shared/services/navigation.service';
import { EventEmitter, Injectable, Output } from '@angular/core';
import { WebApiService } from 'src/app/shared/services/web-api.service';
import { Page } from 'src/app/shared/models/page';
import { Observable, from } from 'rxjs';
import { PagedData, PagedDataUserMenu } from 'src/app/shared/models/paged-data';
import { HttpErrorResponse, HttpParams } from '@angular/common/http';
import { PoTokenAuthService } from 'src/app/shared/services/po-token-auth.service';
import { RfqTokenAuthService } from 'src/app/shared/services/rfq-token-auth.service';
import { menuPageParams, PageParams } from 'src/app/shared/common/Enums';
import { map } from 'rxjs/operators';
import { ApiUrlConstants } from 'src/app/shared/common/ApiUrlConstants';
import { CookieService } from 'ngx-cookie-service';
import { Router } from '@angular/router';
import * as moment from 'moment';
import { FullLoaderService } from 'src/app/shared/services/full-loader.service';
import {
  AddUserResponse,
  ChangeUserPasswordForm,
  ForgotPassForm,
  ForgotPasswordResponse,
  LoginForm,
  LoginResponse,
  NewPassAfterForgotPassForm,
  ResetPassForm,
  SignUpForm,
} from 'src/app/shared/models/user.model';
import { UserCategory } from 'src/app/shared/models/user-category.model';
import { UserMenu } from 'src/app/shared/models/user-menu.model';
import { UserMenuPage } from 'src/app/shared/models/user-menu-page';
import {
  AccountDataType,
  Category,
  Currency,
  AutoCompleteDataType,
  aboutDataType,
  supplierBranchData,
  supplierCatalogueProduct,
  supplierPortData,
  informationType,
  Brand,
  ImpaDataType,
  MtmlUom,
  ServiceApprovalClass,
  Vessel,
  AutoCompleteVesselFilterDataType,
  buyerAboutDataType,
  AutoCompleteSupplierProduct,
  Tag,
} from 'src/app/shared/models/settings.model';
import { PrivateFileService } from '../../shared/services/private-file.service';

export interface UserCookieObj {
  name: string;
  email: string;
}

@Injectable({
  providedIn: 'root',
})
export class SettingsService {
  constructor(
    private privateFiles: PrivateFileService,
    private webApiService: WebApiService,
    private cookieService: CookieService,
    private router: Router,
    private fullLoaderService: FullLoaderService,
    private navigationService: NavigationService,
    private snack: MatSnackBar,
    private poTokenAuth: PoTokenAuthService,
    private rfqTokenAuth: RfqTokenAuthService
  ) {}

  /**
   * Save/Update the user's profile photo URL.
   * Sends the uploaded S3 URL to backend to persist in DB.
   */
  saveUserProfilePhotoUrl(userId: number, photoUrl: string): Promise<any> {
    const payload = { userId, photoUrl };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateProfilePhotoUrl,
      payload
    );
  }

  getGeolocation(geoAddress: string): Promise<any> {
    const address = { geoAddress };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getGeoLocation,
      address
    );
  }

  fetchAllCountryDetails():Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllCountryDetails
    );
  }

  fetchAllCountryDetailsByRfqToken():Promise<any> {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.getRequestWithOptions<any>(
      ApiUrlConstants.getAllCountryDetailsByRfqToken,
      options
    );
  }

  addIncotermsData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addIncotermsData,
      reqBody
    );
  }

  addModeOfTransport(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addModeOfTransport,
      reqBody
    );
  }

  addModeOfTransportToFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addModeOfTransportToFreightForwarder,
      reqBody
    );
  }

  fetchModeofTransportByUserId(userId: number):Promise<any> {
     const url = `${ApiUrlConstants.getModeofTransportByUserId}/${userId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllModeOfTransport(): Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllModeOfTransport
    );
  }

  addPackaging(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPackaging,
      reqBody
    );
  }

  addPackagingToFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPackagingToFreightForwarder,
      reqBody
    );
  }

  fetchPackagingByUserId(userId: number):Promise<any> {
     const url = `${ApiUrlConstants.getPackagingByUserId}/${userId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllPackaging(): Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllPackaging
    );
  }
  
  fetchAllIncotermsBySupplierId(supplierId: number):Promise<any> {
     const url = `${ApiUrlConstants.getAllIncotermsBySupplierId}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllIncotermsBySupplierIdByRfqToken():Promise<any> {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.getRequestWithOptions<any>(
      ApiUrlConstants.getAllIncotermsBySupplierIdByRfqToken,
      options
    );
  }

  fetchSupplierPaymentDetails(supplierId: number):Promise<any> {
     const url = `${ApiUrlConstants.fetchSupplierPaymentDetails}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getSetupBundle(params: {
    userId: number | string;
    userRole: string;
    supplierId?: number | string;
    targetId?: number | string;
  }): Promise<any> {
    const qs = new URLSearchParams();
    qs.set('userId', String(params.userId));
    qs.set('userRole', params.userRole || '');
    if (params.supplierId != null) qs.set('supplierId', String(params.supplierId));
    if (params.targetId != null) qs.set('targetId', String(params.targetId));
    return this.webApiService.getRequest<any>(`${ApiUrlConstants.getSetupBundle}?${qs.toString()}`);
  }

  getIncotermsBundle(supplierId: number | string): Promise<any> {
    const qs = new URLSearchParams();
    if (supplierId != null) qs.set('supplierId', String(supplierId));
    return this.webApiService.getRequest<any>(`${ApiUrlConstants.getIncotermsBundle}?${qs.toString()}`);
  }

  addPaymentDetails(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPaymentDetails,
      reqBody
    );
  }

  updatePaymentDetails(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.updatePaymentDetails,
      reqBody
    );
  }

  getAccountData(): Promise<AccountDataType> {
    return this.webApiService.getRequest<AccountDataType>(
      ApiUrlConstants.getAccountData
    );
  }


  getAccountDataById(supplierId: number): Promise<AccountDataType> {
    const url = `${ApiUrlConstants.getAccountData}/${supplierId}`;
    return this.webApiService.getRequest<AccountDataType>(url);
  }

  getAboutData(): Promise<aboutDataType> {
    return this.webApiService.getRequest<aboutDataType>(
      ApiUrlConstants.getAboutData
    );
  }
  // to fetch about data of specific supplier
  getAboutDataById(supplierId: number): Promise<aboutDataType> {
    const url = `${ApiUrlConstants.getAboutData}/${supplierId}`;
    return this.webApiService.getRequest<aboutDataType>(url);
  }

  getBuyerAboutData(): Promise<buyerAboutDataType> {
    return this.webApiService.getRequest<buyerAboutDataType>(
      ApiUrlConstants.getBuyerAboutData
    );
  }
  // to fetch about data of specific supplier
  getBuyerAboutDataById(buyerId: number): Promise<buyerAboutDataType> {
    const url = `${ApiUrlConstants.getBuyerAboutData}/${buyerId}`;
    return this.webApiService.getRequest<buyerAboutDataType>(url);
  }

  getSupplierPorts(_params: any): Promise<supplierPortData> {
    return this.webApiService.postRequest<supplierPortData>(
      ApiUrlConstants.getSupplierPortData,
      _params
    );
  }

  getSupplierPortsById(_params: any): Promise<supplierPortData> {
    return this.webApiService.postRequest<supplierPortData>(
      ApiUrlConstants.getSupplierPortDataById,
      _params
    );
  }

  // MPS-287 | Akarsh Singh | 2026-08-26
  // Admin queue of branches submitted by buyers, and the decision endpoint.
  getBuyerBranchApprovals(status?: string): Promise<any> {
    const query = status ? `?status=${encodeURIComponent(status)}` : '';
    return this.webApiService.getRequest<any>(`/supplier/buyerBranchApprovalList${query}`);
  }

  updateBuyerBranchApproval(body: any): Promise<any> {
    return this.webApiService.postRequest<any>('/supplier/buyerBranchApproval', body);
  }

  getSupplierBranches(usertype: string): Promise<supplierBranchData[]> {
    const url = `${ApiUrlConstants.getSupplierBranchData}/${usertype}`;
    return this.webApiService.getRequest<supplierBranchData[]>(url);
  }

  getSupplierBranchesById(
    supplierId: number,
    usertype: string
  ): Promise<supplierBranchData[]> {
    const url = `${ApiUrlConstants.getSupplierBranchData}/${usertype}/${supplierId}`;
    return this.webApiService.getRequest<supplierBranchData[]>(url);
  }

  getAllCatalogues(): Promise<supplierCatalogueProduct[]> {
    return this.webApiService.getRequest<supplierCatalogueProduct[]>(
      ApiUrlConstants.supplierCatalogue
    );
  }

  getAllCataloguesById(
    supplierId: number
  ): Promise<supplierCatalogueProduct[]> {
    const url = `${ApiUrlConstants.supplierCatalogue}/${supplierId}`;
    return this.webApiService.getRequest<supplierCatalogueProduct[]>(url);
  }

  getAllProducts(
    searchTerm: string,
    pageMetaData: Page,
    isImpa?: boolean,
    category?: boolean
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set('category', category || false)
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set(PageParams.isImpa, isImpa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(ApiUrlConstants.products, params)
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages,
            _pageMetadata.size,
            paginatedMaster.count
          );
          // console.log('PaginatedMaster Data-> ',paginatedMaster);
          return paginatedMaster;
        })
      );
  }

  getBulkAttachmentUrls(body: any): Promise<any> {
    // Create a deep copy of the request body to avoid modifying the original
    const reqBody = JSON.parse(JSON.stringify(body));
    // remove the data url to reduce the size of the request body
    reqBody.map((attachment: any) => {
      delete attachment.file;
      delete attachment.dataUrl;
    });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getBulkProductAttachmentUrls,
      reqBody
    );
  }

  getBulkAttachmentUrlsByRfqToken(body: any): Promise<any> {
    const reqBody = JSON.parse(JSON.stringify(body));
    reqBody.map((attachment: any) => {
      delete attachment.file;
      delete attachment.dataUrl;
    });
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getBulkProductAttachmentUrlsByRfqToken,
      reqBody,
      options
    );
  }

  addProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createProduct,
      reqBody
    );
  }

  addMySupplierProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addMySupplierProduct,
      reqBody
    );
  }

  requestAddNewSupplierProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.requestAddNewSupplierProduct,
      reqBody
    );
  }

  requestAddNewBuyerProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.requestAddNewBuyerProduct,
      reqBody
    );
  }

  fetchAllBuyerProductChangeRequest(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllBuyerProductChangeRequest,
      params
    );
  }

  approveBuyerProductChangeRequest(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.approveBuyerProductChangeRequest,
      params
    );
  }

  rejectBuyerProductChangeRequest(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.rejectBuyerProductChangeRequest,
      params
    );
  }


  requestUpdateSupplierProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.requestUpdateSupplierProduct,
      reqBody
    );
  }

  // ── Spare parts (MPS-225) ──────────────────────────────────────────
  // Author: Akarsh Singh | 07-08-2026 | Separate calls so the product ones
  // above keep their exact contract and cannot be disturbed.
  requestAddNewSpareProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.requestAddNewSpareProduct,
      reqBody
    );
  }

  requestUpdateSpareProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.requestUpdateSpareProduct,
      reqBody
    );
  }

  /**
   * Author: Akarsh Singh | 10-08-2026
   * Lists spare-part submissions for the approval screens. Read-only — it
   * cannot approve or reject. Pass an admin operation type for the full queue
   * or a supplier one for a single supplier's own submissions.
   * Returns { rows, totalElements }.
   */
  fetchAllSpareChangeRequest(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllSpareChangeRequest,
      reqBody
    );
  }

  /**
   * Author: Akarsh Singh | 10-08-2026
   * Approves a spare part, promoting it into the live catalogue. The backend
   * promotes from the stored submission, so only the approval id is sent.
   */
  approveSpareChangeRequest(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.approveSpareChangeRequest,
      reqBody
    );
  }

  /**
   * Author: Akarsh Singh | 10-08-2026
   * Rejects a spare part. A written reason is mandatory and is passed on to
   * the supplier verbatim.
   */
  rejectSpareChangeRequest(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.rejectSpareChangeRequest,
      reqBody
    );
  }

  /** Returns { isSparePart, spareProduct, specifications, attachments }.
   *  isSparePart false simply means the product is an ordinary one. */
  getSpareProductDetails(approvalId: number): Promise<any> {
    const params = new HttpParams().set('approvalId', approvalId);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.getSpareProductDetails,
      params
    );
  }

  /**
   * Attribute definitions for a product template ('store-product' or
   * 'spare-part'). Drives the extra fields on the form, so an admin adding an
   * attribute does not require a front-end release.
   */
  /** Platform visibility rules + their live impact. Admin screen. */
  getVisibilityRules(): Promise<any> {
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.getVisibilityRules,
      new HttpParams()
    );
  }

  /** Admin-only: flip one gate on a rule. */
  updateVisibilityRule(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateVisibilityRule,
      reqBody
    );
  }

  getProductTemplate(templateKey: string, categoryId?: number): Promise<any> {
    let params = new HttpParams().set('templateKey', templateKey);
    if (categoryId) {
      params = params.set('categoryId', String(categoryId));
    }
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.getProductTemplate,
      params
    );
  }

  
  validateProductName(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.validateProductName,
      reqBody
    );
  }

  deleteProduct(productId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.products}/${productId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  deleteSupplierProduct(productId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.supplierProduct}/${productId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  deleteSupplierService(productId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.supplierService}/${productId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  assignProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignProduct,
      reqBody
    );
  }

  assignService(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignService,
      reqBody
    );
  }

  addCategory(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createCategory,
      reqBody
    );
  }

  updateCategory(reqBody: any, categoryId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.updateCategory}/${categoryId}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  fetchAllProductCategory(
    filterValue: string,
    limit: number,
    offset: number
  ): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.searchParam, filterValue)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllProductCategory,
      params
    );
  }

  fetchAllArchivedProductCategory(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchArchivedProductCategory,
      params
    );
  }

  fetchAllServiceCategory(
    filterValue: string,
    limit: number,
    offset: number
  ): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false)
      .set(PageParams.searchParam, filterValue);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllServiceCategory,
      params
    );
  }

  fetchArchivedServiceCategory(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchArchivedServiceCategory,
      params
    );
  }

  /**
   * Pending product categories awaiting admin approval.
   * Author: Akarsh Singh | 2026-08-19 — searchParam added so an admin can find a
   * specific request instead of paging through the whole queue.
   */
  fetchAllPendingProductCategory(
    limit: number,
    offset: number,
    searchParam: string = ''
  ): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false)
      .set('searchParam', searchParam || '');
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllPendingProductCategory,
      params
    );
  }

  fetchAllPendingServiceCategory(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllPendingServiceCategory,
      params
    );
  }

  deleteCategory(categoryId: number) {
    let apiUrl = `${ApiUrlConstants.fetchAllProductCategory}/${categoryId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  restoreArchivedCategory(categoryId: number) {
    let apiUrl = `${ApiUrlConstants.restoreArchivedCategory}/${categoryId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  approveCategory(categoryId: number) {
    let apiUrl = `${ApiUrlConstants.approveCategory}/${categoryId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  bulkApproveCategories(categoryIds: number[]): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.bulkApproveCategory,
      { categoryIds }
    );
  }

  addBrand(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createBrand,
      reqBody
    );
  }   
  addUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addUomMtmlUomData,
      reqBody
    );
  }   
  addPortByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPortByAdmin,
      reqBody
    );
  }   
  importPortsByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importPortsByAdmin,
      reqBody
    );
  }
  updatePortByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.updatePortByAdmin,
      reqBody
    );
  }  

  importUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importUomMtmlUomData,
      reqBody
    );
  }

  updateUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.updateUomMtmlUomData,
      reqBody
    );
  }  

  restorePortByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.restorePortByAdmin,
      reqBody
    );
  } 

  archiveOrUnarchiveUomMtmlUomData(reqBody: any): Promise<any> {
    return this.webApiService.putRequest<any>(
      ApiUrlConstants.archiveOrUnarchiveUomMtmlUomData,
      reqBody
    );
  } 



  updateBrand(reqBody: any, brandId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.updateBrand}/${brandId}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  fetchAllBrand(
    filterValue: string,
    limit: number,
    offset: number
  ): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.searchParam, filterValue)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllBrand,
      params
    );
  }

  fetchAllArchivedbrand(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchArchivedBrand,
      params
    );
  }

  fetchAllPendingbrand(limit: number, offset: number): Promise<any> {
    let _pageMetadata = new Page();

    let params = new HttpParams()
      .set(PageParams.limit, limit)
      .set(PageParams.page, offset + 1)
      .set(PageParams.isImpa, false);
    return this.webApiService.getRequest1<any>(
      ApiUrlConstants.fetchAllPendingBrand,
      params
    );
  }

  deleteBrand(brandId: number) {
    let apiUrl = `${ApiUrlConstants.fetchAllBrand}/${brandId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  restoreArchivedBrand(brandId: number) {
    let apiUrl = `${ApiUrlConstants.restoreArchivedBrand}/${brandId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  approveBrand(brandId: number) {
    let apiUrl = `${ApiUrlConstants.approveBrand}/${brandId}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  updateProduct(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.products}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteAttachment(attachmentArr: number[], section:string): Promise<any> {
   
    const payload = {
      attachmentArr,
      section
    };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.productAttachment,
      payload
    );
  }

  deleteServiceAttachment(attachmentArr: number[]): Promise<any> {
    const attachments = { attachmentArr };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.ServiceAttachment,
      attachments
    );
  }

  deleteServiceCertificate(certificateattachmentArr: number[]): Promise<any> {
    const attachments = { certificateattachmentArr };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.DeleteServiceCertificate,
      attachments
    );
  }

  fetchServiceCertificateById(certificateId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchServiceCertificateById}/${certificateId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getProductsForSupplier(id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.productsBySupplier}+/${id}`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  // getSupplierData():Promise<any>{
  //   let apiUrl = `${ApiUrlConstants.getSupplierData}`
  //   return this.webApiService.getRequest<any>(apiUrl)
  // }

  addCatalogueProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.catalogueProductOp,
      reqBody
    );
  }

  updateCatalogueProduct(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.catalogueProductOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  updateSupplierBranch(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.supplierBranchOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  createSupplierBranch(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.supplierBranchOp,
      reqBody
    );
  }

  deleteSupplierBranch(id: number): Promise<any> {
    const apiUrl = `${ApiUrlConstants.supplierBranchOp}/${id}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  createVessel(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addUpdateVessel,
      reqBody
    );
  }

  importVessels(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importVessels,
      reqBody
    );
  }

  /** MPS-220 — permanently remove a buyer vessel, capturing the mandatory
   *  deletion reason that is forwarded to the admin alert. */
  deleteVessel(id: number, buyerId: number, reason: string): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.deleteVessel, {
      id,
      buyerId,
      reason,
    });
  }

  fetchVesselDataById(
    userId: number,
    columnName: string,
    columnValue: string
  ): Promise<any> {
    const url = `${ApiUrlConstants.fetchVesselDataByUserId}/${userId}`;
    let params = new HttpParams()
      .set('columnName', columnName)
      .set('columnValue', columnValue);
    return this.webApiService.getRequest<any>(url, params);
  }

  fetchVesselFilterValues(userId: number, columnName: string): Promise<any> {
    const url = `${ApiUrlConstants.fetchVesselFilterValues}/${userId}/${columnName}`;
    return this.webApiService.getRequest<any>(url);
  }

  getSustainabilityData(supplierId: number): Promise<any> {
    let params = new HttpParams().set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.sustainabilityOp,
      params
    );
  }

  addOrganizationalPurpose(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.organizationalPurposeOp,
      reqBody
    );
  }

  updateOrganizationalPurpose(reqBody: any, supplierId: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.organizationalPurposeOp}/${supplierId}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteOrganizationalPurpose(supplierId: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.organizationalPurposeOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  uploadISOCertificateData(data: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.isoCertificationOp}`;
    return this.webApiService.postRequest<any>(apiUrl, data);
  }

  updateISOCertificateData(data: any, id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.isoCertificationOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, data);
  }

  deleteISOCertificateData(supplierId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.isoCertificationOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  addSustainabilityCommitment(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.sustainabilityCommitmentOp,
      reqBody
    );
  }
  importSustainableData(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importSustainableData,
      reqBody
    );
  }

  updateSustainabilityCommitment(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.sustainabilityCommitmentOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteSustainabilityCommitment(
    supplierId: any,
    commitmentId: number
  ): Promise<any> {
    let params = new HttpParams().set('commitmentId', commitmentId);
    let apiUrl = `${ApiUrlConstants.sustainabilityCommitmentOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl, params);
  }

  uploadUNGCParticipationData(data: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.ungcParticipationOp}`;
    return this.webApiService.postRequest<any>(apiUrl, data);
  }

  updateUNGCParticipationData(data: any, id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.ungcParticipationOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, data);
  }

  deleteUNGCParticipationData(supplierId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.ungcParticipationOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  uploadAdditionalDocumentsData(data: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.additionalDocumentsOp}`;
    return this.webApiService.postRequest<any>(apiUrl, data);
  }

  updateAdditionalDocumentsData(data: any, id: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.additionalDocumentsOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, data);
  }

  deleteAdditionalDocumentsData(supplierId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.additionalDocumentsOp}/${supplierId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  addRelevantLinks(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.relevantLinksOp,
      reqBody
    );
  }

  updateRelevantLinks(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.relevantLinksOp}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  deleteRelevantLinks(linkId: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.relevantLinksOp}/${linkId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  async saveCompanyLogoUrl(_params: any, companyId: number) {
    let apiUrl = `/users/uploadCompanyLogo/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async updateCompanyLogoUrl(_params: any, logoId: number) {
    let apiUrl = `/users/updateCompanyLogo/${logoId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async saveCompanyProfileLogoUrl(_params: any, companyId: number) {
    let apiUrl = `/users/uploadCompanyProfileLogo/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async updateCompanyProfileLogoUrl(_params: any, logoId: number) {
    let apiUrl = `/users/updateCompanyProfileLogo/${logoId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async saveSupplierSummaryVideo(_params: any) {
    let apiUrl = '/users/uploadSummaryVideo';
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async updateSupplierSummaryVideo(_params: any, videoId: number) {
    let apiUrl = `/users/updateSummaryVideo/${videoId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateUserData(_params: any, userId: number) {
    let apiUrl = `/users/update/${userId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateBuyerUserData(_params: any, userId: number) {
    let apiUrl = `/users/updateBuyerUser/${userId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  // Platform Management Type (Self-Managed vs Managed by MPS Procurement Team) + supplier contact email
  async updatePlatformManagement(
    isMpsManaged: boolean,
    platformContactEmail: string,
    userId: number
  ) {
    let apiUrl = `/users/updateBuyerUser/${userId}`;
    return this.webApiService.putRequest(apiUrl, {
      isMpsManaged,
      platformContactEmail,
    });
  }

  async updateCompanyAddress(_params: any, companyId: number) {
    let apiUrl = `/users/updateCompany/${companyId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async updateCompanyName(_params: any, companyId: number) {
    let apiUrl = `/users/updateCompanyName/${companyId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  // Submit an organization-name-change request to the admin team. The backend
  // validates the new name and emails the admins (the name is not changed
  // directly). Returns { success, message }.
  async requestOrganizationNameChange(payload: {
    currentOrganizationName: string;
    newOrganizationName: string;
  }) {
    const apiUrl = `/settings/organization-name-change-request`;
    return this.webApiService.postRequest<any>(apiUrl, payload);
  }

  async updateGstNumber(_params: any, companyId: number) {
    let apiUrl = `/users/updateGstNumber/${companyId}`;
    return this.webApiService.putRequest(apiUrl, _params);
  }

  async uploadMembershipData(_params: any) {
    let apiUrl = `/users/memberships`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async uploadCertificateData(_params: any) {
    let apiUrl = `/users/certificates`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async deleteCertificate(certificateId: any) {
    let apiUrl = `/company/certificate/${certificateId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }

  async uploadMembershipDataById(_params: any, companyId: number) {
    let apiUrl = `/users/memberships/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async uploadCertificateDataById(_params: any, companyId: number) {
    let apiUrl = `/users/certificates/${companyId}`;
    return this.webApiService.postRequest(apiUrl, _params);
  }

  async deleteMembership(membershipId: any) {
    let apiUrl = `/company/membership/${membershipId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }

  async createPorts(array: any[]) {
    let apiUrl = `/port/bulkAdd`;
    // return false;
    return this.webApiService.postRequest(apiUrl, array);
  }

  async deletePort(portId: any) {
    let apiUrl = `/port/delete/${portId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }
  async deletePortByAdmin(portId: any):Promise<any> {
    let apiUrl = `/port/deletePortByAdmin/${portId}`;
    return this.webApiService.deleteRequest(apiUrl);
  }

  getCertificatesList(): Promise<informationType[]> {
    return this.webApiService.getRequest<informationType[]>(
      ApiUrlConstants.getCertificates
    );
  }

  getCertificatesListbyId(companyId: number): Promise<informationType[]> {
    const url = `${ApiUrlConstants.getCertificates}/${companyId}`;
    return this.webApiService.getRequest<informationType[]>(url);
  }

  getMembershipList(): Promise<informationType[]> {
    return this.webApiService.getRequest<informationType[]>(
      ApiUrlConstants.getMemberships
    );
  }

  getMembershipListbyId(companyId: number): Promise<informationType[]> {
    const url = `${ApiUrlConstants.getMemberships}/${companyId}`;
    return this.webApiService.getRequest<informationType[]>(url);
  }

  getSearchKeywordProducts(
    searchKeyword: any,
    supplierId?: number,
    pageMetaData?: Page
  ): Observable<PagedData<supplierCatalogueProduct>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', searchKeyword)
      .set('supplierId', supplierId || 0);

    return this.webApiService
      .getPaginatedRequest<PagedData<supplierCatalogueProduct>>(
        ApiUrlConstants.catalogueProductOp,
        params
      )
      .pipe(
        map((paginedProducts) => {
          // Set page metadata for frontend table properties
          paginedProducts.page.totalElements = paginedProducts.count;
          paginedProducts.page.size = _pageMetadata.size;
          paginedProducts.page.totalPages = Math.floor(
            paginedProducts.count / _pageMetadata.size
          );
          paginedProducts.page.pageNumber = _pageMetadata.pageNumber;
          paginedProducts.page.serachParam = _pageMetadata.serachParam;

          return paginedProducts;
        })
      );
    // console.log(searchKeyword);
    // let apiUrl = `/settings/catalogue/products/${searchKeyword}`;
    // return this.webApiService.getRequest<any>(apiUrl);
  }

  getAllCurrenciesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<Currency>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<Currency>>(
        ApiUrlConstants.getAllCurrenciesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCurrency) => {
          // Set page metadata for frontend table properties
          paginedCurrency.page.totalElements = paginedCurrency.count;
          paginedCurrency.page.size = _pageMetadata.size;
          paginedCurrency.page.totalPages = Math.floor(
            paginedCurrency.count / _pageMetadata.size
          );
          paginedCurrency.page.pageNumber = _pageMetadata.pageNumber;
          paginedCurrency.page.serachParam = _pageMetadata.serachParam;

          return paginedCurrency;
        })
      );
  }

  getAllCurrenciesByFliterKeyValueByRfqToken(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<Currency>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService
      .getPaginatedRequestWithOptions<PagedData<Currency>>(
        ApiUrlConstants.getAllCurrenciesByFliterKeyValueByRfqToken,
        params,
        options
      )
      .pipe(
        map((paginedCurrency) => {
          paginedCurrency.page.totalElements = paginedCurrency.count;
          paginedCurrency.page.size = _pageMetadata.size;
          paginedCurrency.page.totalPages = Math.floor(
            paginedCurrency.count / _pageMetadata.size
          );
          paginedCurrency.page.pageNumber = _pageMetadata.pageNumber;
          paginedCurrency.page.serachParam = _pageMetadata.serachParam;
          return paginedCurrency;
        })
      );
  }

  getAllCategoriesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllCategoriesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllSubCategoriesByFliterKeyValue(
    _filterValue: any,
    parentId: number,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false)
      .set('id', parentId);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllSubCategoriesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  
  getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierId(
    _filterValue: any,
    supplierId: number,
    pageMetaData?: Page,
    impa?: boolean,
  ): Observable<PagedData<AutoCompleteSupplierProduct>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set("supplierId",supplierId)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteSupplierProduct>>(
        ApiUrlConstants.getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierId,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierIdByRfqToken(
    _filterValue: any,
    supplierId: number,
    pageMetaData?: Page,
    impa?: boolean,
  ): Observable<PagedData<AutoCompleteSupplierProduct>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set("supplierId",supplierId)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService
      .getPaginatedRequestWithOptions<PagedData<AutoCompleteSupplierProduct>>(
        ApiUrlConstants.getAllAutocompleteSupplierProductByFliterKeyValueAndSupplierIdByRfqToken,
        params,
        options
      )
      .pipe(
        map((paginedCategory) => {
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllServiceCategoriesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllServiceCategoriesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllUomByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<Category>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllUomByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllBrandsByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<Brand>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<Category>>(
        ApiUrlConstants.getAllBrandsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedBrand) => {
          // Set page metadata for frontend table properties
          paginedBrand.page.totalElements = paginedBrand.count;
          paginedBrand.page.size = _pageMetadata.size;
          paginedBrand.page.totalPages = Math.floor(
            paginedBrand.count / _pageMetadata.size
          );
          paginedBrand.page.pageNumber = _pageMetadata.pageNumber;
          paginedBrand.page.serachParam = _pageMetadata.serachParam;

          return paginedBrand;
        })
      );
  }

  getAllAutoCompleteVesselsByFliterValue(
    _filterValue: any,
    buyerId: number,
    pageMetaData?: Page
  ): Observable<PagedData<Vessel>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('buyerId', buyerId)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<Vessel>>(
        ApiUrlConstants.getAllAutoCompleteVesselsByFliterValue,
        params
      )
      .pipe(
        map((paginedCurrency) => {
          // Set page metadata for frontend table properties
          paginedCurrency.page.totalElements = paginedCurrency.count;
          paginedCurrency.page.size = _pageMetadata.size;
          paginedCurrency.page.totalPages = Math.floor(
            paginedCurrency.count / _pageMetadata.size
          );
          paginedCurrency.page.pageNumber = _pageMetadata.pageNumber;
          paginedCurrency.page.serachParam = _pageMetadata.serachParam;

          return paginedCurrency;
        })
      );
  }

  getAllCountriesByFliterKeyValue(): Observable<
    PagedData<AutoCompleteDataType>
  > {
    let params = new HttpParams();
    // .set(PageParams.limit, _pageMetadata.size)
    // .set(PageParams.page, _pageMetadata.pageNumber + 1)
    // .set("cou", _filterValue)

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllCountriesByFilterKeyValue,
        params
      )
      .pipe(
        map((paginatedCountries) => {
          return paginatedCountries;
        })
      );
  }

  getAllRegionsByFliterKeyValue(
    _countryCode: string
  ): Observable<PagedData<AutoCompleteDataType>> {
    let params = new HttpParams().set('countryCode', _countryCode);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllRegionsByFilterKeyValue,
        params
      )
      .pipe(
        map((paginedRegions) => {
          return paginedRegions;
        })
      );
  }

  getAllCitiesByFliterKeyValue(
    _stateCode: string,
    _countryCode: string
  ): Observable<PagedData<AutoCompleteDataType>> {
    let params = new HttpParams()
      .set('countryCode', _countryCode)
      .set('stateCode', _stateCode);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllCitiesByFilterKeyValue,
        params
      )
      .pipe(
        map((paginedCities) => {
          return paginedCities;
        })
      );
  }

  getAllCountryRegionCities() {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getAllCountryRegionCities
    );
  }

  getAllMtmlUomByFliterKeyValue(
    _uomId: number
  ): Observable<PagedData<MtmlUom>> {
    let params = new HttpParams().set('uomId', _uomId);

    return this.webApiService
      .getPaginatedRequest<PagedData<MtmlUom>>(
        ApiUrlConstants.getAllMtmlUomByFliterKeyValue,
        params
      )
      .pipe(
        map((paginedCities) => {
          return paginedCities;
        })
      );
  }

  getAllServiceApprovalClassByFliterValue(
    _filterValue: any,
    pageMetaData?: Page,
    impa?: boolean
  ): Observable<PagedData<ServiceApprovalClass>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('isImpa', impa || false);

    return this.webApiService
      .getPaginatedRequest<PagedData<ServiceApprovalClass>>(
        ApiUrlConstants.getAllServiceApprovalClassByFliterValue,
        params
      )
      .pipe(
        map((paginedCategory) => {
          // Set page metadata for frontend table properties
          paginedCategory.page.totalElements = paginedCategory.count;
          paginedCategory.page.size = _pageMetadata.size;
          paginedCategory.page.totalPages = Math.floor(
            paginedCategory.count / _pageMetadata.size
          );
          paginedCategory.page.pageNumber = _pageMetadata.pageNumber;
          paginedCategory.page.serachParam = _pageMetadata.serachParam;

          return paginedCategory;
        })
      );
  }

  getAllMembershipsByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllMembershipsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedMemberships) => {
          // Set page metadata for frontend table properties
          paginatedMemberships.page.totalElements = paginatedMemberships.count;
          paginatedMemberships.page.size = _pageMetadata.size;
          paginatedMemberships.page.totalPages = Math.floor(
            paginatedMemberships.count / _pageMetadata.size
          );
          paginatedMemberships.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMemberships.page.serachParam = _pageMetadata.serachParam;

          return paginatedMemberships;
        })
      );
  }

  getAllCertificatesByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllCertificatesByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedCertificates) => {
          // Set page metadata for frontend table properties
          paginatedCertificates.page.totalElements =
            paginatedCertificates.count;
          paginatedCertificates.page.size = _pageMetadata.size;
          paginatedCertificates.page.totalPages = Math.floor(
            paginatedCertificates.count / _pageMetadata.size
          );
          paginatedCertificates.page.pageNumber = _pageMetadata.pageNumber;
          paginatedCertificates.page.serachParam = _pageMetadata.serachParam;

          return paginatedCertificates;
        })
      );
  }

  getAllPortsByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllPortsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedPorts) => {
          // Set page metadata for frontend table properties
          paginatedPorts.page.totalElements = paginatedPorts.count;
          paginatedPorts.page.size = _pageMetadata.size;
          paginatedPorts.page.totalPages = Math.floor(
            paginatedPorts.count / _pageMetadata.size
          );
          paginatedPorts.page.pageNumber = _pageMetadata.pageNumber;
          paginatedPorts.page.serachParam = _pageMetadata.serachParam;

          return paginatedPorts;
        })
      );
  }

  getAllSDGCommitmentsByFliterKeyValue(
    _filterValue: any,
    _commitmentsDeclared: any[],
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue)
      .set('_commitmentsDeclared', JSON.stringify(_commitmentsDeclared));

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllSDGCommitmentsByFliterKeyValue,
        params
      )
      .pipe(
        map((paginatedCommitments) => {
          // Set page metadata for frontend table properties
          paginatedCommitments.page.totalElements = paginatedCommitments.count;
          paginatedCommitments.page.size = _pageMetadata.size;
          paginatedCommitments.page.totalPages = Math.floor(
            paginatedCommitments.count / _pageMetadata.size
          );
          paginatedCommitments.page.pageNumber = _pageMetadata.pageNumber;
          paginatedCommitments.page.serachParam = _pageMetadata.serachParam;
          console.log('returning sdg commitments==>', paginatedCommitments);
          return paginatedCommitments;
        })
      );
  }

  getAllVesselFilterValuesByFliterKeyValue(
    _filterValue: any,
    filterKey: any,
    userId: number,
    pageMetaData?: Page
  ): Observable<PagedData<AutoCompleteVesselFilterDataType>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterKey', filterKey)
      .set('filterValue', _filterValue)
      .set('userId', userId);

    return this.webApiService
      .getPaginatedRequest<PagedData<AutoCompleteVesselFilterDataType>>(
        ApiUrlConstants.getAllAutocompleteVesselFilterData,
        params
      )
      .pipe(
        map((paginatedPorts) => {
          // Set page metadata for frontend table properties
          paginatedPorts.page.totalElements = paginatedPorts.count;
          paginatedPorts.page.size = _pageMetadata.size;
          paginatedPorts.page.totalPages = Math.floor(
            paginatedPorts.count / _pageMetadata.size
          );
          paginatedPorts.page.pageNumber = _pageMetadata.pageNumber;
          paginatedPorts.page.serachParam = _pageMetadata.serachParam;

          return paginatedPorts;
        })
      );
  }

  async downloadS3FileAsBlob(
    url: string,
    fileName: string = 'fileName.ext'
  ): Promise<File> {
    // 2026-10-25 (S3 step 2): never an HttpClient GET straight to a private S3 file; use a signed link.
    const readable = await this.privateFiles.resolve(url);
    if (!readable) throw new Error('File not available');
    const blob: any = await this.webApiService.getS3FileAsBlob(readable);
    return new File([blob], fileName, { type: blob.type });
  }

  getImpaData(): Promise<any> {
    return this.webApiService.getRequest<any>(ApiUrlConstants.getImpaData);
  }

  /**
   * Author: Akarsh Singh | 10-08-2026
   * A supplier's APPROVED spare parts only, for the Spare parts filter.
   * Deliberately the same signature and return shape as getImpaDataById, so
   * the product list can switch between the two without any other change.
   */
  getSupplierSpareProducts(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);

    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getSupplierSpareProducts,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          return paginatedMaster;
        })
      );
  }

  getImpaDataById(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.supplierImpaproduct,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          return paginatedMaster;
        })
      );
  }

  // service api
  getServiceData(): Promise<any> {
    return this.webApiService.getRequest<any>(ApiUrlConstants.getServiceData);
  }

  getPortsByAdmin(
    searchParam?: string,
    sortDirection?: string,
    sortField?: string,
    limit: number = 10,
    offset: number = 0,
    isActive:number=1,
    isDelete:number=0
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getPortsByAdmin,
      {
        searchParam,
        sortDirection,
        sortField,
        limit,
        offset,
        isActive,
        isDelete
      }
    );
  }       
  getUomMtmlUomData(
    searchParam?: string,
    sortDirection?: string,
    sortField?: string,
    limit: number = 10,
    offset: number = 0,
    isActive:number=1,
    isDelete:number=0,
    
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getUomMtmlUomData,
      {
        searchParam,
        sortDirection,
        sortField,
        limit,
        offset,
        isActive,
        isDelete
        
      }
    );
  } 
  /** Full port roster. Pass forceFresh for the export download, which must not
   *  be answered from a cached snapshot; the admin ports list reads the same
   *  roster for its own purposes and is happy with the held copy. */
  exportPortsByAdmin(forceFresh: boolean = false): Promise<any> {
    const refreshQuery = forceFresh ? '?refresh=1' : '';
    return this.webApiService.getRequest<any>(
      `${ApiUrlConstants.exportPortsByAdmin}${refreshQuery}`
    );
  }

  /** Full UoM & MTLM UoM roster. Pass forceFresh for the export download,
   *  which must not be answered from a cached snapshot; the Active / Inactive
   *  stat tiles read the same roster and are happy with the held copy. */
  exportUomMtmlUomData(forceFresh: boolean = false): Promise<any> {
    const refreshQuery = forceFresh ? '?refresh=1' : '';
    return this.webApiService.getRequest<any>(
      `${ApiUrlConstants.exportUomMtmlUomData}${refreshQuery}`
    );
  }

  getServiceDataById(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getSupplierServiceData,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          return paginatedMaster;
        })
      );
  }

  getAllServiceData(
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getAllServiceData,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          return paginatedMaster;
        })
      );
  }

  addService(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createService,
      reqBody
    );
  }

  addServiceCertificate(reqBody: any, serviceId: number): Promise<any> {
    const url = `${ApiUrlConstants.createServiceCertificate}/${serviceId}`;
    return this.webApiService.postRequest<any>(url, reqBody);
  }

  deleteService(serviceId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.getServiceData}/${serviceId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  updateService(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.createService}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  updateServiceCertificate(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.updateServiceCertificateById}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  requestAddNewSupplierService(reqBody: any): Promise<any> {
    let apiUrl = `/services/requestAddNewSupplierService`
    return this.webApiService.postRequest<any>(
      apiUrl,
      reqBody
    );
  }

  requestUpdateSupplierService(reqBody: any): Promise<any> {
    let apiUrl = `/services/requestUpdateSupplierService`
    return this.webApiService.postRequest<any>(
      apiUrl,
      reqBody
    );
  }

  approveSupplierServiceChangeRequest(reqBody: any): Promise<any> {
     let apiUrl = `/services/approveSupplierServiceChangeRequest`
    return this.webApiService.postRequest<any>(
     apiUrl,
      reqBody
    );
  }

  // service api ends

  getBrandCategory(params: string, supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.getBrandCategory}/${params}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getSearchSupplierEmail(
    index: number,
    searchParam: any,
    userCategory: any
  ): Promise<any> {
    // console.log(searchKeyword);
    let apiUrl = `/search/supplierLists/validate?index=${index}&searchParam=${searchParam}&userCategory=${userCategory}`;

    return this.webApiService.getRequest<any>(apiUrl);
  }

  addCompanyProductServiceDescription(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addCompanyProductServiceDescription,
      reqBody
    );
  }

  getProductServiceDescriptionById(companyId: number): Promise<any> {
    const url = `${ApiUrlConstants.getProductServiceDescription}/${companyId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getCatalogueDataById(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page,
    type: string
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId)
      .set('type', type);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getCatalogueDataById,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          console.log(paginatedMaster.count, _pageMetadata.size);
          return paginatedMaster;
        })
      );
  }

  addCatalogue(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.CreateCatalogue,
      reqBody
    );
  }

  deleteCatalogue(catalogueId: number): Promise<any> {
    let apiUrl = `${ApiUrlConstants.getCatalogueDataById}/${catalogueId}`;
    return this.webApiService.deleteRequest<any>(apiUrl);
  }

  updateCatalogue(reqBody: any, id: any): Promise<any> {
    let apiUrl = `${ApiUrlConstants.CreateCatalogue}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  getSearchImpaItems(searchTerm: string, itemType: string) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('itemType', itemType);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchImpaItems,
      params
    );
  }

  getSearchImpaItemsMySupplier(searchTerm: string, itemType: string, userId: number) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('itemType', itemType)
      .set('userId', userId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchImpaItemsMySupplier,
      params
    );
  }

  getSearchCountryPorts(searchTerm: string, itemType: string) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('itemType', itemType);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getSearchCountryPorts,
      params
    );
  }

  getSupplierProfileData(supplierId: any): Promise<any> {
    const params = { supplierId };
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getSupplierProfileData,
      params
    );
  }

  getLimitedProfileData(params: string, supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.getLimitedProfileData}/${params}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getSearchCategoryItems(searchTerm: string, supplierId: any) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchCategoryItems,
      params
    );
  }

  getSearchBrandItems(searchTerm: string, supplierId: any) {
    let params = new HttpParams()
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.searchBrandItems,
      params
    );
  }

  getArchivedItems(getFor: string, supplierId: number): Promise<any> {
    let params = new HttpParams()
      .set('getFor', getFor)
      .set('supplierId', supplierId);
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getArchivedItems,
      params
    );
  }

  getDetailedArchivedItems(
    getFor: string,
    supplierId: number,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let params = new HttpParams()
      .set('getFor', getFor)
      .set('supplierId', supplierId)
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1);

    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        ApiUrlConstants.getDetailedArchivedItems,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;

          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages,
            _pageMetadata.size,
            paginatedMaster.count
          );
          // console.log('PaginatedMaster Data-> ',paginatedMaster);
          return paginatedMaster;
        })
      );
  }

  restoreArchivedItems(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.restoreArchivedItems,
      params
    );
  }

  inviteUserBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.inviteUserBySupplier,
      reqBody
    );
  }

  updateUserDetails(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateUserDetails,
      reqBody
    );
  }


  fetchUserBySupplierId(
    supplierId: number,
    limit: number,
    offset: number,
    searchParam: string = ''
  ): Observable<{ rows: any[]; totalElements: number }> {
    const apiUrl = `${
      ApiUrlConstants.fetchUserBySupplier
    }?supplierId=${supplierId}&limit=${limit}&offset=${offset}&searchParam=${encodeURIComponent(
      searchParam
    )}`;

    return from(
      this.webApiService.getRequest1<{ rows: any[]; totalElements: number }>(
        apiUrl
      )
    );
  }

  fetchUserListBundle(reqBody: {
    supplierId: number;
    limit: number;
    offset: number;
    searchParam?: string;
    userId: number;
  }): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchUserListBundle,
      reqBody
    );
  }

  getImpaDetailsforRfq(impaId: number): Promise<any> {
    const url = `${ApiUrlConstants.getImpaDetailsforRfq}/${impaId}`;
    return this.webApiService.getRequest<any>(url);
  }

  getCardData(obj: any): Promise<any> {
    const name = obj.name;

    const type = obj.type;

    const url = `/settings/getCardData/${name}/${type}`;

    return this.webApiService.getCardDataRequest<any>(url);
  }

  getSupplierCount(catName: any): Promise<any> {
    const url = `${ApiUrlConstants.getSupplierCount}/${catName}`;

    return this.webApiService.getRequest<any>(url);
  }

  getDashboardSupplierCounts(): Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getDashboardSupplierCounts
    );
  }

  getSupplierCountByBrand(brandName: string): Promise<any> {
    const url = `${ApiUrlConstants.getSupplierCountByBrand}/${brandName}`;

    return this.webApiService.getRequest<any>(url);
  }

  getSupplierCountByServiceCategory(catName: any): Promise<any> {
    const url = `${ApiUrlConstants.getSupplierCountByServiceCategory}/${catName}`;

    return this.webApiService.getRequest<any>(url);
  }

  fetchSetupEmailsByUserId(userId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchSetupEmailsByUserId}/${userId}`;
    return this.webApiService.getRequest<any>(url);
  }

  addRecentSearchHistory(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addRecentSearchHistory,
      reqBody
    );
  }

  fetchUserSearchHistory(userId?: number, sessionId?: string): Promise<any> {
    let params = new HttpParams()
      .set('userId', userId || '')
      .set('sessionId', sessionId || '');
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.fetchUserSearchHistory,
      params
    );
  }

  createSupplierSetup(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createSupplierSetup,
      reqBody
    );
  }

  fetchSupplierSetup(section: string, supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchSupplierSetup}/${section}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchSupplierSetupByRfqToken(section: string): Promise<any> {
    const url = `${ApiUrlConstants.fetchSupplierSetupByRfqToken}/${section}`;
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.getRequestWithOptions<any>(url, options);
  }

  fetchBuyerNotificationSettings(buyerId: number): Promise<any> {
    const url = `${ApiUrlConstants.getBuyerNotificationSettings}/${buyerId}`;
    return this.webApiService.getRequest<any>(url);
  }

  createBuyerNotificationSettings(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createBuyerNotificationSettings,
      reqBody
    );
  }

  updateBuyerNotificationSettings(buyerId: number, reqBody: any): Promise<any> {
    const url = `${ApiUrlConstants.updateBuyerNotificationSettings}/${buyerId}`;
    return this.webApiService.putRequest<any>(url, reqBody);
  }

  // Supplier notification settings (dedicated endpoints)
  fetchSupplierNotificationSettings(supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchSupplierNotificationSettings}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  createSupplierNotificationSettings(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createSupplierNotificationSettings,
      reqBody
    );
  }

  updateSupplierNotificationSettings(supplierId: number, reqBody: any): Promise<any> {
    const url = `${ApiUrlConstants.updateSupplierNotificationSettings}/${supplierId}`;
    return this.webApiService.putRequest<any>(url, reqBody);
  }

  /**
   * The supplier's "Also, get notified on" addresses, up to five.
   * Akarsh Singh | 2026-10-17
   *
   * Kept separate from the notification-settings calls above because the
   * flags and the address list are edited and saved independently; folding
   * the list into that payload would mean every flag save rewrote the
   * addresses as well.
   */
  fetchSupplierNotificationEmails(supplierId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchSupplierNotificationEmails}/${supplierId}`;
    return this.webApiService.getRequest<any>(url);
  }

  saveSupplierNotificationEmails(supplierId: number, emails: string[]): Promise<any> {
    const url = `${ApiUrlConstants.saveSupplierNotificationEmails}/${supplierId}`;
    return this.webApiService.putRequest<any>(url, { supplierId, emails });
  }

  fetchBuyerNotificationEmails(buyerId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchBuyerNotificationEmails}/${buyerId}`;
    return this.webApiService.getRequest<any>(url);
  }

  saveBuyerNotificationEmails(buyerId: number, emails: string[]): Promise<any> {
    const url = `${ApiUrlConstants.saveBuyerNotificationEmails}/${buyerId}`;
    return this.webApiService.putRequest<any>(url, { buyerId, emails });
  }

  // guestSessionId: a guest's own history id (the API updates only the caller's own row, 2026-10-23)
  UpdateUserSearchHistory(id: any, guestSessionId?: string | null): Promise<any> {
    let apiUrl = `${ApiUrlConstants.UpdateUserSearchHistory}/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, guestSessionId ? { sessionId: guestSessionId } : {});
  }

  fetchUserInbox(
    supplierId: number,
    options?: { lastSentAt?: string | null; lastId?: number | string | null; pageSize?: number; archived?: boolean }
  ): Promise<any> {
    const params: string[] = [];
    if (options?.lastSentAt) params.push(`lastSentAt=${encodeURIComponent(options.lastSentAt)}`);
    if (options?.lastId !== undefined && options?.lastId !== null && options?.lastId !== '')
      params.push(`lastId=${encodeURIComponent(String(options.lastId))}`);
    if (options?.pageSize) params.push(`pageSize=${encodeURIComponent(String(options.pageSize))}`);
    // ?archived=true returns the Archived view instead of the active inbox.
    if (options?.archived) params.push(`archived=true`);
    const qs = params.length ? `?${params.join('&')}` : '';
    const url = `${ApiUrlConstants.fetchUserInbox}/${supplierId}${qs}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchBranchAddressFilterValues(
    buyerId: number,
    addressType: number
  ): Promise<any> {
    const url = `${ApiUrlConstants.fetchBranchAddressFilterValues}/${buyerId}/${addressType}`;
    return this.webApiService.getRequest<any>(url);
  }

  getUnreadMailCountByUserId() {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getUnreadMailCount
    );
  }

  getTotalMailCount() {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getTotalMailCount
    );
  }

  getTotalMailCountByInterval(interval:string) {
    const url = `${ApiUrlConstants.getTotalMailCountByInterval}/${interval}`;
    return this.webApiService.getRequest<any>(url);
  }

  markAsRead(mailId: number) {
    const url = `${ApiUrlConstants.markAsRead}/${mailId}`;
    return this.webApiService.getRequest<any>(url);
  }

  /** Archive (or unarchive) a single inbox email for the signed-in user.
   *  Author: Akarsh Singh. */
  archiveMail(mailId: number, unarchive = false) {
    const url = `${ApiUrlConstants.archiveMail}/${mailId}${unarchive ? '?unarchive=true' : ''}`;
    return this.webApiService.getRequest<any>(url);
  }

  fetchAllRfqBySupplierId(
    filterValue: string,
    supplierId: number,
    // docStatus: string,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllRfqBySupplierId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        supplierId,
        docStatus,
        fromDate,
        toDate,
        isActive,
      }
    );
  }

  fetchAllServiceRfqBySupplierId(
    filterValue: string,
    supplierId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    page: number,
    isActive: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllServiceRfqBySupplierId,
      {
        limit,
        page,
        filterValue,
        sortField,
        sortDir,
        supplierId,
        docStatus,
        fromDate,
        toDate,
        isActive,
      }
    );
  }

  fetchAllServiceRfqByBuyerId(
    filterValue: string,
    buyerId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    page: number,
    isActive: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllServiceRfqByBuyerId,
      {
        limit,
        page,
        filterValue,
        sortField,
        sortDir,
        buyerId,
        docStatus,
        fromDate,
        toDate,
        isActive,
      }
    );
  }

  archiveServiceRfq(
    supplierServiceRfqDetailsId: number,
    supplierId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.archiveServiceRfq,
      {
        supplierServiceRfqDetailsId,
        supplierId,
      }
    );
  }

  unArchiveServiceRfq(
    supplierServiceRfqDetailsId: number,
    supplierId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.unArchiveServiceRfq,
      {
        supplierServiceRfqDetailsId,
        supplierId,
      }
    );
  }

  bulkArchiveServiceRfq(
    supplierServiceRfqDetailsIds: number[],
    supplierId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.bulkArchiveServiceRfq,
      {
        supplierServiceRfqDetailsIds,
        supplierId,
      }
    );
  }

  bulkUnArchiveServiceRfq(
    supplierServiceRfqDetailsIds: number[],
    supplierId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.bulkUnArchiveServiceRfq,
      {
        supplierServiceRfqDetailsIds,
        supplierId,
      }
    );
  }

  buyerArchiveServiceRfq(
    serviceRfqId: number,
    buyerId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.buyerArchiveServiceRfq,
      { serviceRfqId, buyerId }
    );
  }

  buyerUnArchiveServiceRfq(
    serviceRfqId: number,
    buyerId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.buyerUnArchiveServiceRfq,
      { serviceRfqId, buyerId }
    );
  }

  buyerBulkArchiveServiceRfq(
    serviceRfqIds: number[],
    buyerId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.buyerBulkArchiveServiceRfq,
      { serviceRfqIds, buyerId }
    );
  }

  buyerBulkUnArchiveServiceRfq(
    serviceRfqIds: number[],
    buyerId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.buyerBulkUnArchiveServiceRfq,
      { serviceRfqIds, buyerId }
    );
  }

  cancelBuyerServiceRfq(
    serviceRfqId: number,
    buyerId: number,
    rfqStatus: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.cancelBuyerServiceRfq,
      { serviceRfqId, buyerId, rfqStatus }
    );
  }

  generateBuyerServiceRfqAttachment(
    serviceRfqId: number,
    buyerId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateBuyerServiceRfqAttachment,
      { serviceRfqId, buyerId }
    );
  }

  fetchBuyerServiceRfqDetailsForExcel(
    serviceRfqId: number,
    buyerId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchBuyerServiceRfqDetailsForExcel,
      { serviceRfqId, buyerId }
    );
  }

  exportAllServiceRfq(
    filterValue: string,
    supplierId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    isActive: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.exportAllServiceRfq,
      {
        filterValue,
        supplierId,
        docStatus,
        fromDate,
        toDate,
        isActive,
      }
    );
  }

  exportBuyerServiceRfq(
    filterValue: string,
    buyerId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    isActive: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.exportBuyerServiceRfq,
      {
        filterValue,
        buyerId,
        docStatus,
        fromDate,
        toDate,
        isActive,
      }
    );
  }

  // =============================================
  // Service RFQ User Assignment Methods
  // =============================================
  assignUserToServiceRfq(
    serviceRfqId: number,
    assignedUserId: number,
    assignedBy: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignUserToServiceRfq,
      {
        serviceRfqId,
        assignedUserId,
        assignedBy,
      }
    );
  }

  unassignUserFromServiceRfq(
    serviceRfqId: number,
    assignedUserId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.unassignUserFromServiceRfq,
      {
        serviceRfqId,
        assignedUserId,
      }
    );
  }

  getAssignedUsersForServiceRfq(serviceRfqId: number): Promise<any> {
    return this.webApiService.getRequest<any>(
      `${ApiUrlConstants.getAssignedUsersForServiceRfq}/${serviceRfqId}`
    );
  }

  bulkAssignUsersToServiceRfq(
    serviceRfqId: number,
    userIds: string,
    assignedBy: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.bulkAssignUsersToServiceRfq,
      {
        serviceRfqId,
        userIds,
        assignedBy,
      }
    );
  }

  // =============================================
  // Buyer Service RFQ User Assignment Methods
  // =============================================
  assignUserToBuyerServiceRfq(
    serviceRfqId: number,
    assignedUserId: number,
    assignedBy: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignUserToBuyerServiceRfq,
      {
        serviceRfqId,
        assignedUserId,
        assignedBy,
      }
    );
  }

  unassignUserFromBuyerServiceRfq(
    serviceRfqId: number,
    assignedUserId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.unassignUserFromBuyerServiceRfq,
      {
        serviceRfqId,
        assignedUserId,
      }
    );
  }

  getAssignedUsersForBuyerServiceRfq(serviceRfqId: number): Promise<any> {
    return this.webApiService.getRequest<any>(
      `${ApiUrlConstants.getAssignedUsersForBuyerServiceRfq}/${serviceRfqId}`
    );
  }

  bulkAssignUsersToBuyerServiceRfq(
    serviceRfqId: number,
    userIds: string,
    assignedBy: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.bulkAssignUsersToBuyerServiceRfq,
      {
        serviceRfqId,
        userIds,
        assignedBy,
      }
    );
  }

  generateSupplierServiceRfqAttachment(
    serviceRfqId: number,
    supplierId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateSupplierServiceRfqAttachment,
      {
        serviceRfqId,
        supplierId,
      }
    );
  }

  fetchServiceRfqDetailsForExcel(
    serviceRfqId: number,
    supplierId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServiceRfqDetailsForExcel,
      {
        serviceRfqId,
        supplierId,
      }
    );
  }

  fetchQuoteDetailsBySupplierId(
    filterValue: string,
    supplierId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    if (!limit) limit = 10;
    if (!offset) offset = 0;
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchQuoteDetailsBySupplierId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        supplierId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  fetchServiceQuoteDetailsBySupplierId(
    filterValue: string,
    supplierId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    if (!limit) limit = 10;
    if (!offset) offset = 0;
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServiceQuoteDetailsBySupplierId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        supplierId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  fetchAllServiceQuotesByBuyerId(
    filterValue: string,
    buyerId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    if (!limit) limit = 10;
    if (!offset) offset = 0;
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllServiceQuotesByBuyerId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        buyerId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  buyerServiceQuoteOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.buyerServiceQuoteOperations,
      params
    );
  }

  fetchServiceQuoteLineItemsByBuyer(id: number): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServiceQuoteLineItemsByBuyer,
      { id }
    );
  }

  assignUserToBuyerServiceQuotes(
    quoteIds: any,
    buyerId: number,
    buyerUserId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignUserToBuyerServiceQuotes,
      { quoteIds, buyerId, buyerUserId }
    );
  }

  fetchBuyerServiceQuoteExportAll(buyerId: number): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchBuyerServiceQuoteExportAll,
      { buyerId }
    );
  }

  serviceQuoteOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.serviceQuoteOperations,
      params
    );
  }

  assignUserToServiceQuotes(
    quoteIds: any,
    assignedBy: number,
    assignedUserId: number
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignUserToServiceQuotes,
      {
        quoteIds,
        assignedBy,
        assignedUserId,
      }
    );
  }

  fetchServiceQuoteAttachmentExcelData(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServiceQuoteAttachmentExcelData,
      params
    );
  }

  generateServiceQuotationAttachment(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateServiceQuotationAttachment,
      params
    );
  }

  fetchServiceQuoteDetailsForBuyer(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServiceQuoteDetailsForBuyer,
      params
    );
  }

  fetchServiceQuoteListByRfqId(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServiceQuoteListByRfqId,
      params
    );
  }

  createServicePurchaseOrder(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.createServicePurchaseOrder,
      params
    );
  }

  // MPS-208: buyer-side Service Supplier Quote Comparison page data
  getCompareServiceSupplierQuotes(reqBody: { serviceRfqId: number; quoteIds: number[] }): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getCompareServiceSupplierQuotes,
      reqBody
    );
  }

  fetchServicePurchaseOrder(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServicePurchaseOrder,
      params
    );
  }

  updateServicePurchaseOrder(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateServicePurchaseOrder,
      params
    );
  }

  updateServicePurchaseOrderFreightForwarder(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateServicePurchaseOrderFreightForwarder,
      params
    );
  }

  sendServicePoConfirmationForBuyerAndSupplier(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.sendServicePoConfirmationForBuyerAndSupplier,
      params
    );
  }

  fetchAllServicePurchaseOrderByBuyerId(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllServicePurchaseOrderByBuyerId,
      params
    );
  }

  servicePurchaseOrderListOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.servicePurchaseOrderListOperations,
      params
    );
  }

  generateServicePoPdf(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateServicePoPdf,
      params
    );
  }

  fetchServicePoAttachmentExcelData(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchServicePoAttachmentExcelData,
      params
    );
  }

  assignServicePOToBuyerUser(poIds: any, buyerUserId: number): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignServicePOToBuyerUser,
      { poIds, buyerUserId }
    );
  }

  fetchAllServicePurchaseOrderBySupplierId(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllServicePurchaseOrderBySupplierId,
      params
    );
  }

  supplierServicePurchaseOrderListOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.supplierServicePurchaseOrderListOperations,
      params
    );
  }

  sendServicePOConfirmationAcknowledgement(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.sendServicePOConfirmationAcknowledgement,
      params
    );
  }

  sendServicePORejectionAcknowledgement(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.sendServicePORejectionAcknowledgement,
      params
    );
  }

  getLastAcceptedServiceOrderId(): Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.getLastAcceptedServiceOrderId
    );
  }

  fetchAllServiceRejectionReason(): Promise<any> {
    return this.webApiService.getRequest<any>(
      ApiUrlConstants.fetchAllServiceRejectionReason
    );
  }

  // Service Invoice methods
  createAndGetServiceInvoiceDetails(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.createAndGetServiceInvoiceDetails, params);
  }

  sendServiceInvoice(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.sendServiceInvoice, params);
  }

  uploadServicePOD(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.uploadServicePOD, params);
  }

  generateServiceCommercialInvoicePdf(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.generateServiceCommercialInvoicePdf, params);
  }

  generateServicePackingListPdf(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.generateServicePackingListPdf, params);
  }

  generateServiceDeliveryNotePdf(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.generateServiceDeliveryNotePdf, params);
  }

  generateServiceInvoicePdf(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.generateServiceInvoicePdf, params);
  }

  fetchServiceInvoiceAttachmentExcelData(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.fetchServiceInvoiceAttachmentExcelData, params);
  }

  fetchServiceInvoiceStatusSummaryByBuyerId(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.fetchServiceInvoiceStatusSummaryByBuyerId, params);
  }

  fetchServiceInvoiceListsByBuyerId(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.fetchServiceInvoiceListsByBuyerId, params);
  }

  fetchArchivedServiceInvoiceListByBuyerId(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.fetchArchivedServiceInvoiceListByBuyerId, params);
  }

  exportAllServiceInvoiceListByBuyerId(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.exportAllServiceInvoiceListByBuyerId, params);
  }

  addServiceInvoicePaymentDetails(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.addServiceInvoicePaymentDetails, params);
  }

  serviceInvoiceListsOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.serviceInvoiceListsOperations, params);
  }

  fetchAllRfqBybuyerId(
    filterValue: string,
    buyerId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllRfqBybuyerId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        buyerId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  fetchSupplierUserByRfqId(rfqId: number): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchSupplierUserByRfqId,
      {
        rfqId,
      }
    );
  }
  assignRfqSupplierUser(
    rfqIds: any,
    supplierId: number,
    supplierUserId: number
  ): Promise<any> {
    console.log({ rfqIds });
    console.log({ supplierId });
    console.log({ supplierUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignSupplierUser,
      {
        rfqIds,
        supplierId,
        supplierUserId,
      }
    );
  }
  
  assignRFQToBuyerUser(
    rfqIds: any,
    buyerId: number,
    buyerUserId: number
  ): Promise<any> {
    console.log({ rfqIds });
    console.log({ buyerId });
    console.log({ buyerUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignRFQToBuyerUser,
      {
        rfqIds,
        buyerId,
        buyerUserId,
      }
    );
  }

  assignPOToBuyerUser(
    poIds: any,
    buyerId: number,
    buyerUserId: number
  ): Promise<any> {
    console.log({ poIds });
    console.log({ buyerId });
    console.log({ buyerUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignPOToBuyerUser,
      {
        poIds,
        buyerId,
        buyerUserId,
      }
    );
  }

  assignPOToSupplierUser(
    poIds: any,
    supplierId: number,
    supplierUserId: number
  ): Promise<any> {
    console.log({ poIds });
    console.log({ supplierId });
    console.log({ supplierUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignPOToSupplierUser,
      {
        poIds,
        supplierId,
        supplierUserId,
      }
    );
  }

        
  assignQuoteToSupplierUser(
    quoteIds: any,
    supplierId: number,
    supplierUserId: number
  ): Promise<any> {
    console.log({ quoteIds });
    console.log({ supplierId });
    console.log({ supplierUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignQuoteToSupplierUser,
      {
        quoteIds,
        supplierId,
        supplierUserId,
      }
    );
  }

  async generateSupplierRfqAttachment(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateSupplierRfqAttachment,
      params
    );
  }

  async generateSupplierRfqAttachmentByRfqToken(params: any) {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateSupplierRfqAttachmentByRfqToken,
      params,
      options
    );
  }

  rfqOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.rfqOperations,
      params
    );
  }
  rfqOperationsByRfqToken(params: any): Promise<any> {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.rfqOperationsByRfqToken,
      params,
      options
    );
  }
  buyerRfqOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.buyerRfqOperations,
      params
    );
  }

  sendRfqRemindersByBuyer(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.sendRfqRemindersByBuyer,
      reqBody
    );
  }

  sendServiceRfqRemindersByBuyer(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      '/services/rfq/sendServiceRfqRemindersByBuyer',
      reqBody
    );
  }

  addAdditionalSupplierToServiceRfq(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      '/services/rfq/addAdditionalSupplierToServiceRfq',
      params
    );
  }

  sendServiceRfqAcknowledgementForNewSuppliers(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      '/services/rfq/sendServiceRfqAcknowledgementForNewSuppliers',
      params
    );
  }

  quoteOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.quoteOperations,
      params
    );
  }

  addPortAgent(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addPortAgent,
      reqBody
    );
  }

  addFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addFreightForwarder,
      reqBody
    );
  }

  importPortAgent(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importPortAgent,
      reqBody
    );
  }

  importFreightForwarder(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importFreightForwarder,
      reqBody
    );
  }

  fetchPortAgentByUserId(userId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchPortAgentByUserId}/${userId}`;
    return this.webApiService.getRequest1<any>(url);
  }

  fetchFreightForwarderByUserId(userId: number): Promise<any> {
    const url = `${ApiUrlConstants.fetchFreightForwarderByUserId}/${userId}`;
    return this.webApiService.getRequest1<any>(url);
  }

  // on Suppllier's User page
  importUsersBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importUsers,
      reqBody
    );
  }
  fetchAllUsersBySupplierId(supplierId: number): Observable<{ rows: any[] }> {
    const apiUrl = `${ApiUrlConstants.exportAllUsers}?supplierId=${supplierId}`;
    return from(this.webApiService.getRequest1<{ rows: any[] }>(apiUrl));
  }

  importProductsByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importProducts,
      reqBody
    );
  }
  /**
   * Admin bulk upload of spare parts to the master catalogue. Each row is
   * staged and released through the same approval path a single spare part
   * uses, so the import cannot bypass the approval rules.
   */
  importSparePartsByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importSparePartsByAdmin,
      reqBody
    );
  }
  importProductsByBuyer(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importProductsByBuyer,
      reqBody
    );
  }
  importAllImpaDataBySupplierId(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importAllImpaDataBySupplierId,
      reqBody
    );
  }
  importMarketplaceProductDataBySupplierId(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importMarketplaceProductDataBySupplierId,
      reqBody
    );
  }
  importServicesByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServicesByAdmin,
      reqBody
    );
  }

  importServicesByBuyer(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServicesByBuyer,
      reqBody
    );
  }
  importServicesBySupplierId(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServicesBySupplierId,
      reqBody
    );
  }

  exportAllProductsByAdmin(): Observable<{ rows: any[] }> {
    // let apiUrl = `/products/exportAllProducstByAdmin`;
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportProducts
      )
    );
  }

  exportAllProductsByBuyer(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllProductsByBuyer
      )
    );
  }

  exportAllImpaDataBySupplierId(
    category: boolean,
    isImpa: boolean,
    supplierId: number
  ): Observable<{ rows: any[] }> {
    const params = new HttpParams()
      .set('category', String(category))
      .set('isImpa', String(isImpa))
      .set('supplierId', String(supplierId));
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllImpaDataBySupplierId,
        params
      )
    );
  }

  exportAllMarketPlaceDataBySupplierId(
    supplierId: number
  ): Observable<{ rows: any[] }> {
    const params = new HttpParams()
      .set('supplierId', String(supplierId));
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllMarketPlaceDataBySupplierId,
        params
      )
    );
  }

  exportAllServicesByAdmin(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllServicesByAdmin
      )
    );
  }

  exportAllServicesByBuyer(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllServicesByBuyer
      )
    );
  }

  exportServicesBySupplierId(supplierId: number): Observable<{ rows: any[] }> {
    const params = new HttpParams().set('supplierId', String(supplierId));
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportServicesBySupplierId,
        params
      )
    );
  }

  importSuppliersBranches(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importSuppliersBranches,
      reqBody
    );
  }

  exportAllCategoriesByAdmin(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllCategoriesByAdmin
      )
    );
  }

  /** Full brand roster. Pass forceFresh for the export download, for the same
   *  reason as the ports export above. */
  exportAllBrandsByAdmin(forceFresh: boolean = false): Observable<{ rows: any[] }> {
    const refreshQuery = forceFresh ? '?refresh=1' : '';
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        `${ApiUrlConstants.exportAllBrandsByAdmin}${refreshQuery}`
      )
    );
  }
  exportAllServiceCategoriesByAdmin(): Observable<{ rows: any[] }> {
    return from(
      this.webApiService.getRequest<{ rows: any[] }>(
        ApiUrlConstants.exportAllServiceCategoriesByAdmin
      )
    );
  }
  importCategoriesByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importCategoriesByAdmin,
      reqBody
    );
  }
  importBrandsByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importBrandsByAdmin,
      reqBody
    );
  }

  importServiceCategoriesByAdmin(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importServiceCategoriesByAdmin,
      reqBody
    );
  }


  addTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addTaxDetailsBySupplier,
      reqBody
    );
  } 

  importTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.importTaxDetailsBySupplier,
      reqBody
    );
  }

  updateTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.updateTaxDetailsBySupplier,
      reqBody
    );
  }
  getTaxDetailsBySupplier(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getTaxDetailsBySupplier,
      reqBody
    );
  }
  getTaxDetailsBySupplierByPoToken(reqBody: any): Promise<any> {
    const poId = this.poTokenAuth.getPoId();
    const body = {
      ...reqBody,
      ...(poId && !reqBody?.purchaseOrderId ? { purchaseOrderId: poId } : {})
    };
    const options = this.poTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getTaxDetailsBySupplierByPoToken,
      body,
      options
    );
  }

  addTaxDetailsBySupplierByRfqToken(reqBody: any): Promise<any> {
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.addTaxDetailsBySupplierByRfqToken,
      reqBody,
      options
    );
  }

  getAllRegionsByFliterKeyValueByRfqToken(
    _countryCode: string
  ): Observable<PagedData<AutoCompleteDataType>> {
    let params = new HttpParams().set('countryCode', _countryCode);
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService
      .getPaginatedRequestWithOptions<PagedData<AutoCompleteDataType>>(
        ApiUrlConstants.getAllRegionsByFilterKeyValueByRfqToken,
        params,
        options
      )
      .pipe(map((paginedRegions) => paginedRegions));
  }

  getTaxDetailsBySupplierByRfqToken(reqBody: any): Promise<any> {
    const rfqId = this.rfqTokenAuth.getRfqId();
    const body = {
      ...reqBody,
      ...(rfqId && !reqBody?.rfqId ? { rfqId: rfqId } : {})
    };
    const options = this.rfqTokenAuth.buildAuthHeaders();
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.getTaxDetailsBySupplierByRfqToken,
      body,
      options
    );
  }

  fetchAllQuotesByBuyerId(
    filterValue: string,
    buyerId: number,
    docStatus: string,
    fromDate: any,
    toDate: any,
    sortField: string,
    sortDir: string,
    limit: number,
    offset: number,
    isActive: number,
    operationType: string
  ): Promise<any> {
    if (!limit) limit = 10;
    if (!offset) offset = 0;
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchAllQuotesByBuyerId,
      {
        limit,
        offset,
        filterValue,
        sortField,
        sortDir,
        buyerId,
        docStatus,
        fromDate,
        toDate,
        isActive,
        operationType,
      }
    );
  }

  fetchQuoteLineItemDetailsByQuoteId(
    id: number
  ): Promise<any> {
   
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchQuoteLineItemDetailsByQuoteId,
      {
        id
      }
    );
  }

  async generateQuoteAttachment(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateQuoteAttachment,
      params
    );
  }

  buyerQuoteOperations(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.quoteOperationsByBUyer,
      params
    );
  }

  /** Active quote loss reasons for the buyer reject dialog: [{ id, code, label, sortOrder }]. */
  fetchQuoteLossReasons(): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.fetchQuoteLossReasons, {});
  }

  /** The signed-in supplier's own quote outcome: { quoteId, outcome: null | { code, label, reasonCode, reasonLabel, note, at } }. */
  fetchQuoteOutcomeForSupplier(quoteId: number): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.fetchQuoteOutcomeForSupplier, { quoteId });
  }

  /**
   * 2026-10-24 Batch B (P1 / P5): the RFQ lines that match the signed-in supplier's OWN catalogue, with the catalogue
   * price (prefillPrice = same currency, valid dates) and technical files. 404 when the caller was not invited.
   * { rfqId, totalLines, matchedLines, lines: [{ rfqLineItemId, productId, match, price, currencyCode, prefillPrice, priceNote, files }] }
   */
  fetchCatalogueMatchesForRfq(rfqId: number, withFiles = true): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.fetchCatalogueMatchesForRfq, { rfqId, withFiles });
  }

  /** 2026-10-24 Batch B (P8): { portCount } for the signed-in supplier (dashboard prompt). */
  fetchMyPortCoverage(): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.myPortCoverage, {});
  }

  /** 2026-10-24 Batch B (P8): admin report - suppliers by port coverage (platform admins only). */
  fetchAdminPortCoverageReport(params: { coverage?: string; page?: number; limit?: number; search?: string }): Promise<any> {
    return this.webApiService.postRequest<any>(ApiUrlConstants.adminPortCoverageReport, params || {});
  }

  async generateRfqAttachmentForBuyer(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.generateRfqAttachmentForBuyer,
      params
    );
  }

  assignPOApprovalToBuyerUser(
    poIds: any,
    buyerId: number,
    buyerUserId: number
  ): Promise<any> {
    console.log({ poIds });
    console.log({ buyerId });
    console.log({ buyerUserId });
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.assignPOApprovalToBuyerUser,
      {
        poIds,
        buyerId,
        buyerUserId,
      }
    );
  }

  fetchAllDispatchRequirements(): Promise<any> {
    let apiUrl = `/settings/fetchAllDispatchRequirements`;
    return this.webApiService.getRequest<any>(apiUrl);
  }

  createDispatchRequirement(reqBody: any): Promise<any> {
    let apiUrl = `/settings/createDispatchRequirement`;
    return this.webApiService.postRequest<any>(
      apiUrl,
      reqBody
    );
  }


  async fetchSupplierRfqAttachmentExcelData(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchSupplierRfqAttachmentExcelData,
      params
    );
  }

  async fetchBuyerRfqAttachmentExcelData(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchBuyerRfqAttachmentExcelData,
      params
    );
  }

  async fetchQuoteAttachmentExcelData(params: any) {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.fetchQuoteAttachmentExcelData,
      params
    );
  }

  async fetchQuoteExcelTemplateData(params: any) {
    let apiUrl = '/quote/fetchQuoteExcelTemplateData'
    return this.webApiService.postRequest<any>(
      apiUrl,
      params
    );
  }

  addMarketplaceProduct(reqBody: any): Promise<any> {
    let apiUrl = '/products/addMarketplaceProduct'
    return this.webApiService.postRequest<any>(
      apiUrl,
      reqBody
    );
  }

  updateMarketplaceProduct(reqBody: any, id: any): Promise<any> {
    let apiUrl = `/products/updateMarketplaceProduct/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  updateMarketplaceInventoryQuantity(reqBody: any, id: any): Promise<any> {
    let apiUrl = `/products/updateMarketplaceInventoryQuantity/${id}`;
    return this.webApiService.putRequest<any>(apiUrl, reqBody);
  }

  requestAddNewMarketplaceProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.requestAddNewMarketplaceProduct,
      reqBody
    );
  }

  requestUpdateMarketplaceProduct(reqBody: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.requestUpdateMarketplaceProduct,
      reqBody
    );
  }

  getMarketPlaceProductBySupplierId(
    supplierId: any,
    searchTerm: string,
    pageMetaData: Page
  ): Observable<PagedData<any>> {
    let _pageMetadata = new Page();
    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }

    let apirUrl = `/settings/getMarketPlaceProductBySupplierId`
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set(PageParams.searchParam, searchTerm)
      .set('supplierId', supplierId);
    return this.webApiService
      .getPaginatedRequest<PagedData<any>>(
        apirUrl,
        params
      )
      .pipe(
        map((paginatedMaster) => {
          // Set page metadata for frontend table properties
          paginatedMaster.page.totalElements = paginatedMaster.count;
          paginatedMaster.page.size = _pageMetadata.size;
          paginatedMaster.page.totalPages = Math.ceil(
            paginatedMaster.count / _pageMetadata.size
          );
          paginatedMaster.page.pageNumber = _pageMetadata.pageNumber;
          paginatedMaster.page.serachParam = _pageMetadata.serachParam;
          console.log(
            'total pages for search==>',
            paginatedMaster.page.totalPages
          );
          return paginatedMaster;
        })
      );
  }


  assignMarketplaceProduct(reqBody: any): Promise<any> {
    let apiUrl = `/products/assignMarketPlaceProduct`
    return this.webApiService.postRequest<any>(
      apiUrl,
      reqBody
    );
  }

  addNewProductTag(reqBody: any): Promise<any> {
    let apiUrl = `/products/addNewProductTag`
    return this.webApiService.postRequest<any>(
      apiUrl,
      reqBody
    );
  }


  getAllTagsByFliterKeyValue(
    _filterValue: any,
    pageMetaData?: Page
  ): Observable<PagedData<Tag>> {
    let _pageMetadata = new Page();
    let apiUrl = '/settings/getAllProductTagsByFliterValue'

    if (pageMetaData) {
      _pageMetadata = pageMetaData;
    } else {
      _pageMetadata.pageNumber = 0;
      _pageMetadata.size = 10;
    }
    /**
     * Set request params for pagination variables.
     * @pageNumber Add +1 in PageNumber as page start with 0 in frontend data-table.
     */
    let params = new HttpParams()
      .set(PageParams.limit, _pageMetadata.size)
      .set(PageParams.page, _pageMetadata.pageNumber + 1)
      .set('filterValue', _filterValue);

    return this.webApiService
      .getPaginatedRequest<PagedData<Tag>>(
        apiUrl,
        params
      )
      .pipe(
        map((paginedTag) => {
          // Set page metadata for frontend table properties
          paginedTag.page.totalElements = paginedTag.count;
          paginedTag.page.size = _pageMetadata.size;
          paginedTag.page.totalPages = Math.floor(
            paginedTag.count / _pageMetadata.size
          );
          paginedTag.page.pageNumber = _pageMetadata.pageNumber;
          paginedTag.page.serachParam = _pageMetadata.serachParam;

          return paginedTag;
        })
      );
  }

    addAdditionalSUpplierToRfq(params:any): Promise<any> {
      let apirUrl = `/rfq/addAdditionalSUpplierToRfq`
      return this.webApiService.postRequest<any>(
        apirUrl,
        params
      );
    }

    async verifySoruceRfq(rfqId:number):Promise<any>{
      let apiUrl = `/rfq/verifySoruceRfqForCopying/${rfqId}`;
      return this.webApiService.getRequest<any>(apiUrl)
    }
  

    async copyRfq(params: any): Promise<any> {
      let apiUrl = `/rfq/copyRfq`;
      return this.webApiService.postRequest<any>(apiUrl, params
      );
    }



    async fetchAllSupplierChangeRequest(params:any): Promise<any> {
      let apiUrl = `/settings/fetchAllSupplierChangeRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params
      );
    }

    async approveSupplierProductChangeRequest(params:any): Promise<any> {
      let apiUrl = `/products/approveSupplierProductChangeRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params
      );
    }

    async rejectSupplierProductChangeRequest(params:any): Promise<any> {
      let apiUrl = `/settings/rejectSupplierProductChangeRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params
      );
    }

    async rejectSupplierServiceChangeRequest(params:any): Promise<any> {
      let apiUrl = `/settings/rejectSupplierServiceChangeRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params
      );
    }

    async approveMarketplaceProductChangeRequest(params:any): Promise<any> {
      let apiUrl = `/products/approveMarketplaceProductChangeRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params
      );
    }

    async rejectMarketplaceProductChangeRequest(params:any): Promise<any> {
      let apiUrl = `/settings/rejectMarketplaceProductChangeRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params
      );
    }

    // MPS-214: Admin edits the non-price / non-delivery fields of a PENDING approval request
    async updateProductApprovalRequest(params:any): Promise<any> {
      let apiUrl = `/products/updateProductApprovalRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params);
    }

    async updateMarketplaceProductApprovalRequest(params:any): Promise<any> {
      let apiUrl = `/products/updateMarketplaceProductApprovalRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params);
    }

    async updateServiceApprovalRequest(params:any): Promise<any> {
      let apiUrl = `/services/updateServiceApprovalRequest`;
      return this.webApiService.postRequest<any>(apiUrl, params);
    }

    async getApprovalEditAuditTrail(params:any): Promise<any> {
      let apiUrl = `/products/getApprovalEditAuditTrail`;
      return this.webApiService.postRequest<any>(apiUrl, params);
    }

    async fetchAutoAcceptDataBySupplierId(supplierId:number):Promise<any>{
      let apiUrl = `/settings/setup/fetchAutoAcceptDataBySupplierId/${supplierId}`;
      return this.webApiService.getRequest<any>(apiUrl)
    }

    async enableDisableAutoAccept(params:any): Promise<any> {
      let apiUrl = `/settings/setup/enableDisableAutoAccept`;
      return this.webApiService.postRequest<any>(apiUrl, params);
    }


    




  /**
   * Candidate spare parts out of a manufacturer PDF catalogue. Read-only —
   * the caller reviews the rows before any of them are created.
   */
  extractSparePartsFromCatalogue(params: any): Promise<any> {
    return this.webApiService.postRequest<any>(
      ApiUrlConstants.extractSparePartsFromCatalogue,
      params
    );
  }

  
  fetchSuggestedRfqByBuyerId(
    params:any
  ): Promise<any> {
    let apiUrl = `/rfq/fetchSuggestedRfqByBuyerId`
    return this.webApiService.postRequest<any>(
      apiUrl,
      params
    );
  }

  fetchProductPoDataByBuyerId(
    params:any
  ): Promise<any> {
    let apiUrl = `/rfq/fetchProductPoDataByBuyerId`
    return this.webApiService.postRequest<any>(
      apiUrl,
      params
    );
  }

  createRfqFromSuggestedRfq(
    params:any
  ): Promise<any> {
    let apiUrl = `/rfq/createRfqFromSuggestedRfq`
    return this.webApiService.postRequest<any>(
      apiUrl,
      params
    );
  }

  fetchRfqAndQuoteHistoryDataByBuyerId(
    params:any
  ): Promise<any> {
    let apiUrl = `/rfq/fetchRfqAndQuoteHistoryDataByBuyerId`
    return this.webApiService.postRequest<any>(
      apiUrl,
      params
    );
  }
  
  
}
